/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { IonButton, IonCardContent, IonContent, IonIcon, IonPage, IonSpinner } from "@ionic/react";
import { chevronBack, chevronForward, chevronDown, chevronUp } from "ionicons/icons";
import { useHistory, useLocation, useParams } from "react-router";
import { motion, PanInfo } from "framer-motion";
import BiWazeTherapyCard from "../../components/therapy/BiWazeTherapyCard";
import Header from "../../components/Header";
import TabNavigation from "../../components/ui/tabs/TabSelector";
import SegmentedPicker from "../../components/ui/tabs/SegmentSelector";
import CardSkeleton from "../../components/skeleton/CardSkeleton";
import PressureFlowGraph, { transformTherapyData } from "./TherapySetVsDelivered";
import TherapyActivitiesSection from "../../components/home/TherapyActivitiesSection";
import SessionPressureChart from "../../components/therapy/SessionPressureChart";
import ClearSessionPressureChart from "../../components/therapy/ClearSessionPressureChart";
import ClearDaySessionsChart from "../../components/therapy/ClearDaySessionsChart";
import useAuth from "../../hooks/useAuth";
import { useStorage } from "../../contexts/StorageContext";
import { formatDuration } from "../../utils/helpers";
import {
  getFetchPressureSummary,
  getDailyTherapies,
  getWeeklyStatus,
} from "../../services/dashboard";
import {
  getPressureFlowGraphSummaryByDateRange,
  getClearTherapySummary,
  ClearTherapySessionDetail,
  ClearTherapyDataPoint,
} from "../../services/graphs";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
import { getTodayFormatted } from "../../utils/date";
import "./Activity.css";
import InsightsGraphVisx from "../../components/home/InsightsGraphVisx";
import ClearPressureFlowGraph from "../../components/home/ClearPressureFlowGraph";
import DayDatePicker from "../../components/datePicker/DayDatePicker";
import MonthPicker from "../../components/datePicker/MonthPicker";
import YearPicker from "../../components/datePicker/YearPicker";
import WeekRangePicker from "../../components/datePicker/WeekRangePicker";
import {
  DateRange,
  formatSessionTime,
  generateDateRange,
  getPeriodLabel,
  getSessionLabel,
  TimePeriod,
} from "../../utils/dateHelpers";
import { getUserName } from "../../utils/helpers";
import { resolveRequestedDevice } from "./activityDevice";

const ActivityDetails: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { storage, isStorageReady } = useStorage();

  /* ⭐ **THE ADDRESS, READ THROUGH THE ROUTER.** `useParams` gives the `:id` segment of
     `/activity/details/:id` and `useLocation` the query string that goes with it. ⛔ Not
     `window.location.search`: it is not a render input, so React never re-renders when it
     changes, and on a native build the router's location and the document's are not the same
     thing. See `activityDevice.ts` for why the segment is the authority. */
  const { id: deviceRouteParam } = useParams<{ id?: string }>();
  const { search } = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  /** Which system this screen was opened for — `null` only if the address named neither. */
  const requestedDevice = useMemo(
    () => resolveRequestedDevice(deviceRouteParam, search),
    [deviceRouteParam, search]
  );

  // State management
  /* ⭐ **SEEDED FROM THE ADDRESS, NOT FROM `"cough"`.** F-142: the first paint is what the
     patient sees, and a screen that paints Cough and corrects itself on the next tick is a
     screen that showed the wrong system. The literal remains only for an address that named
     no device at all. */
  const [activeTab, setActiveTab] = useState<"cough" | "clear">(
    requestedDevice ?? "cough"
  );
  const [selectedTimePeriod, setSelectedTimePeriod] =
    useState<TimePeriod>("Year");
  const [periodOffset, setPeriodOffset] = useState<number>(0); // 0 = current, -1 = previous, etc.
  const profileIdParam = queryParams.get("profile_id") || "";
  const nameParam = queryParams.get("name") || "";
  const [profileId, setProfileId] = useState<number>(
    profileIdParam ? +profileIdParam : user?.profile_id || 0
  );
  const [name, setName] = useState<string>(nameParam || "");
  /* ⚠️ **RE-APPLIED WHENEVER THE ADDRESS CHANGES, NOT ONCE AT MOUNT.** `IonRouterOutlet` keys
     its views by the matched route, so `/activity/details/cough` and `/activity/details/clear`
     are the SAME view and walking between them never remounts this component. The `[]` this
     effect used to carry is precisely why the second screen kept the first one's panel.
     ⛔ `requestedDevice` only — `activeTab` is deliberately NOT a dependency, so that a patient
     who owns both systems can still switch tabs with the selector without this effect dragging
     them back to the one in the address. */
  useEffect(() => {
    if (requestedDevice) setActiveTab(requestedDevice);
  }, [requestedDevice]);

  const [subTitle, setSubTitle] = useState<string>(nameParam || "");
  const [loading, setLoading] = useState<boolean>(true);
  const [dailyTherapies, setDailyTherapies] = useState<any>(null);
  interface ActivityAdherence {
    therapyData: any[];
    insights: {
      therapies: any[];
      vitals: {
        spo2: any[];
        heart_rate: any[];
        aqi: any[];
      };
    };
    date?: string;
    startDate?: string;
    endDate?: string;
    isYearlyData?: boolean;
    aggregationType?: "Year" | "Month" | "Week";
    devices?: {
      device_id: string;
      device_type: string;
    }[];
    oldestKnownTherapyDate?: { cough: string; clear: string }; // e.g., "2022-01-01" for oldest known therapy day
  }

  const [activityAdherence, setActivityAdherence] = useState<ActivityAdherence>(
    {
      insights: {
        therapies: [],
        vitals: { spo2: [], heart_rate: [], aqi: [] },
      },
      therapyData: [],
    }
  );
  // Track the true oldest therapy date across all API responses (only update to older values)
  const [oldestTherapyDate, setOldestTherapyDate] = useState<{
    cough: string;
    clear: string;
  } | null>(null);

  const [pressureSummary, setPressureSummary] = useState<any>({
    data: [],
    metadata: {},
  });
  // Per-session pressure/flow chart state (Day view, cough only)
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showWeekRangePicker, setShowWeekRangePicker] = useState(false);
  const [pressureFlowData, setPressureFlowData] = useState<Map<string, any[]>>(new Map());
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [sessionMetric, setSessionMetric] = useState<"flow" | "volume">("flow");
  const [pressureFlowLoading, setPressureFlowLoading] = useState(false);
  const [clearSessionsMap, setClearSessionsMap] = useState<Map<string, ClearTherapySessionDetail>>(new Map());
  const [clearSessionsLoading, setClearSessionsLoading] = useState(false);
  const [showDaySummary, setShowDaySummary] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    generateDateRange("Week")
  );
  // Fetch follower name for subtitle
  useEffect(() => {
    const fetchFollowerName = async () => {
      if (user?.profile_id === profileId) {
        setSubTitle(getUserName(user));
        return;
      }
      if (storage && isStorageReady) {
        const storedFollowing = await getIonicStorage(storage, "following");
        // ⛔ `.find` returns undefined when nothing matches — and `getIonicStorage` returns null
        // for a key never written, so on a cold start this list is empty and `follower` is
        // ALWAYS undefined. `follower.profile` then threw. `getUserName` is total and handles
        // undefined itself, so the guard belongs on the deref, not on the call.
        const follower = (storedFollowing || []).find(
          (f: any) => f?.profile?.id === profileId
        );
        setSubTitle(getUserName(follower?.profile));
      } else {
        setSubTitle("");
      }
    };

    fetchFollowerName();
  }, [profileId, user?.profile_id, storage, isStorageReady]);

  // Handle time period change
  function handleTimePeriodChange(period: TimePeriod) {
    setSelectedTimePeriod(period);
    setPeriodOffset(0); // Reset offset when changing period type
  }

  // Compute the minimum allowed offset based on oldest known therapy date
  const minPeriodOffset = useMemo(() => {
    if (!oldestTherapyDate) {
      console.log(
        "[minPeriodOffset] No oldestTherapyDate yet, returning -Infinity"
      );
      return -Infinity;
    }

    const dateStr =
      activeTab === "cough" ? oldestTherapyDate.cough : oldestTherapyDate.clear;
    if (!dateStr) {
      console.log(
        "[minPeriodOffset] No dateStr for tab:",
        activeTab,
        oldestTherapyDate
      );
      return -Infinity;
    }

    const oldestDate = new Date(dateStr);
    if (isNaN(oldestDate.getTime())) {
      console.log("[minPeriodOffset] Invalid date string:", dateStr);
      return -Infinity;
    }

    const today = new Date();
    let result: number;

    switch (selectedTimePeriod) {
      case "Year":
        result = oldestDate.getFullYear() - today.getFullYear();
        break;

      case "Month":
        result =
          (oldestDate.getFullYear() - today.getFullYear()) * 12 +
          (oldestDate.getMonth() - today.getMonth());
        break;

      case "Week": {
        // Get Monday of current week
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        const currentWeekStart = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() - diffToMonday
        );

        // Get Monday of the week containing the oldest date
        const oldDayOfWeek = oldestDate.getDay();
        const oldDiffToMonday = oldDayOfWeek === 0 ? 6 : oldDayOfWeek - 1;
        const oldWeekStart = new Date(
          oldestDate.getFullYear(),
          oldestDate.getMonth(),
          oldestDate.getDate() - oldDiffToMonday
        );

        const diffMs = oldWeekStart.getTime() - currentWeekStart.getTime();
        result = Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000));
        break;
      }

      case "Day": {
        const todayOnly = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        const oldOnly = new Date(
          oldestDate.getFullYear(),
          oldestDate.getMonth(),
          oldestDate.getDate()
        );
        const diffMs = oldOnly.getTime() - todayOnly.getTime();
        result = Math.floor(diffMs / (24 * 60 * 60 * 1000));
        break;
      }

      default:
        result = -Infinity;
    }

    console.log("[minPeriodOffset]", {
      period: selectedTimePeriod,
      tab: activeTab,
      dateStr,
      oldestDate: oldestDate.toISOString(),
      today: today.toISOString(),
      result,
      periodOffset,
      canGoBack: periodOffset > result,
    });

    return isNaN(result) ? -Infinity : result;
  }, [selectedTimePeriod, activeTab, oldestTherapyDate]);

  // Navigate to previous period (swipe right or click left arrow)
  const goToPreviousPeriod = useCallback(() => {
    setPeriodOffset((prev) => {
      if (prev - 1 < minPeriodOffset) return prev;
      return prev - 1;
    });
  }, [minPeriodOffset]);

  // Navigate to next period (swipe left or click right arrow)
  const goToNextPeriod = useCallback(() => {
    if (periodOffset < 0) {
      setPeriodOffset((prev) => prev + 1);
    }
  }, [periodOffset]);

  // Whether we can go further back
  const canGoBack = periodOffset > minPeriodOffset;

  // Handle swipe gesture
  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const swipeThreshold = 50; // minimum swipe distance
      if (info.offset.x > swipeThreshold && canGoBack) {
        // Swiped right - go to previous period
        goToPreviousPeriod();
      } else if (info.offset.x < -swipeThreshold && periodOffset < 0) {
        // Swiped left - go to next period (only if not at current)
        goToNextPeriod();
      }
    },
    [goToPreviousPeriod, goToNextPeriod, periodOffset, canGoBack]
  );

  // Today's date in YYYY-MM-DD format for the date picker maxDate
  const todayISO = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
  }, []);

  // Get the current period label
  const currentPeriodLabel = useMemo(() => {
    return getPeriodLabel(selectedTimePeriod, periodOffset);
  }, [selectedTimePeriod, periodOffset]);

  // Get the month name for the selected period (used in BiWazeTherapyCard)
  const selectedPeriodMonth = useMemo(() => {
    const today = new Date();
    if (selectedTimePeriod === "Month") {
      const monthDate = new Date(
        today.getFullYear(),
        today.getMonth() + periodOffset,
        1
      );
      return monthDate.toLocaleString("default", { month: "long" });
    } else if (selectedTimePeriod === "Year") {
      // For year view, show the year
      return `${today.getFullYear() + periodOffset}`;
    } else if (selectedTimePeriod === "Week") {
      // For week view, show the month of the week's start date
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - diff + periodOffset * 7);
      return weekStart.toLocaleString("default", { month: "long" });
    }
    return today.toLocaleString("default", { month: "long" });
  }, [selectedTimePeriod, periodOffset]);

  // Update dateRange when selectedTimePeriod or periodOffset changes
  useEffect(() => {
    setDateRange(generateDateRange(selectedTimePeriod, periodOffset));
    if (selectedTimePeriod === "Day") {
      fetchDailyTherapies();
      fetchPressureFlowForDay();
      fetchClearSessionsForDay();
      // Reset expanded session when navigating days
      setExpandedSessionId(null);
      setClearSessionsMap(new Map());
    }
  }, [selectedTimePeriod, periodOffset]);

  const fetchDailyTherapies = async () => {
    const dateRange = generateDateRange("Day", periodOffset);
    const cacheKey = `dailyTherapies_${profileId}_${periodOffset}_${activeTab}`;
    const storedAdherence = await getIonicStorage(storage, cacheKey);
    if (storedAdherence) {
      setDailyTherapies(storedAdherence);
    }
    const response = await getDailyTherapies(profileId, dateRange.startDate);
    setDailyTherapies(response);

    await ionicStorage(storage, cacheKey, response);
  };

  const fetchPressureFlowForDay = async () => {
    if (!profileId) return;
    setPressureFlowLoading(true);
    try {
      const dayRange = generateDateRange("Day", periodOffset);
      const response = await getPressureFlowGraphSummaryByDateRange(
        profileId,
        dayRange.startDate,
        dayRange.startDate
      );
      if (response?.initialData) {
        const transformed = transformTherapyData(response.initialData);
        // Group by refId
        const grouped = new Map<string, any[]>();
        transformed.forEach((point: any) => {
          const key = String(point.refId);
          if (!grouped.has(key)) {
            grouped.set(key, []);
          }
          grouped.get(key)!.push(point);
        });
        setPressureFlowData(grouped);
      }
    } catch (error) {
      console.error("Error fetching pressure flow data for day:", error);
    } finally {
      setPressureFlowLoading(false);
    }
  };

  const fetchClearSessionsForDay = async () => {
    if (!profileId) return;
    setClearSessionsLoading(true);
    try {
      const dayRange = generateDateRange("Day", periodOffset);
      const response = await getClearTherapySummary(profileId, dayRange.startDate, dayRange.startDate);
      if (response?.sessions?.length) {
        const map = new Map<string, ClearTherapySessionDetail>();
        response.sessions.forEach((session) => {
          if (session.refId) map.set(String(session.refId), session);
        });
        setClearSessionsMap(map);
      } else {
        setClearSessionsMap(new Map());
      }
    } catch (error) {
      console.error("Error fetching clear sessions for day:", error);
      setClearSessionsMap(new Map());
    } finally {
      setClearSessionsLoading(false);
    }
  };

  // Load activity adherence when dateRange, profileId, storage, or isStorageReady changes
  useEffect(() => {
    const abortController = new AbortController();

    const loadActivityData = async () => {
      if (!storage || !isStorageReady || !profileId) return;
      setLoading(true);
      try {
        const cacheKey = `dailyStatus_${profileId}_${selectedTimePeriod}_${periodOffset}_${activeTab}`;
        const storedActivityAdherence = await getIonicStorage(
          storage,
          cacheKey
        );

        if (storedActivityAdherence?.aggregationType === selectedTimePeriod) {
          setActivityAdherence(storedActivityAdherence);
          if (
            storedActivityAdherence.aggregationType === "Year" &&
            storedActivityAdherence.oldestKnownTherapyDate
          ) {
            setOldestTherapyDate(
              storedActivityAdherence.oldestKnownTherapyDate
            );
          }
          setLoading(false);
        } else {
          setLoading(true);
        }

        // Fetch fresh data - pass the current period, offset and abort signal
        await fetchSummaryData(
          selectedTimePeriod,
          periodOffset,
          abortController.signal
        );
        // await fetchPressureSummary(selectedTimePeriod, abortController.signal);

        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      } catch (error: any) {
        if (error.name === "AbortError") {
          console.log("Request cancelled due to period change");
          return;
        }
        console.error("Error loading activity data:", error);
        setLoading(false);
      }
    };

    loadActivityData();

    // Cleanup: abort pending requests when selectedTimePeriod or periodOffset changes
    return () => {
      abortController.abort();
    };
  }, [selectedTimePeriod, periodOffset]);

  // Fetch daily status
  async function fetchSummaryData(
    period?: TimePeriod,
    offset = 0,
    signal?: AbortSignal
  ) {
    if (!profileId) return;
    const dateRange = generateDateRange(period || "Week", offset);
    console.log(
      "Fetching Summary Data for period:",
      period,
      "offset:",
      offset,
      dateRange
    );

    try {
      const response: {
        therapyData: any[];
        insights: any;
        aggregationType: any;
      } = await getWeeklyStatus(profileId, {
        date: dateRange.endDate, // Use the end date of the range instead of today
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        showPercentage: true,
      });

      // Check if request was aborted
      if (signal?.aborted) {
        console.log("Summary data fetch aborted for period:", period);
        return;
      }

      if (response) {
        // Check if this response is still relevant to the CURRENT selectedTimePeriod and offset
        if (
          response.aggregationType === period &&
          period === selectedTimePeriod &&
          offset === periodOffset
        ) {
          setActivityAdherence(response);
          if (
            response.aggregationType === "Year" &&
            (response as any).oldestKnownTherapyDate
          ) {
            setOldestTherapyDate((response as any).oldestKnownTherapyDate);
          }
          console.log(
            "Fetched Activity Adherence:",
            period,
            "offset:",
            offset,
            response.aggregationType
          );

          const cacheKey = `dailyStatus_${profileId}_${period}_${offset}_${activeTab}`;
          await ionicStorage(storage, cacheKey, response);
        } else {
          console.log(
            "Ignoring stale response for period:",
            period,
            "offset:",
            offset,
            "Current period:",
            selectedTimePeriod,
            "Current offset:",
            periodOffset
          );
        }
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Summary data request cancelled");
        return;
      }
      console.error("Error fetching daily status:", error);
    }
  }

  async function fetchPressureSummary(
    period?: TimePeriod,
    offset = 0,
    signal?: AbortSignal
  ) {
    if (!profileId) return;

    try {
      const dateRange = generateDateRange(period || "Week", offset);
      const cacheKey = `pressureSummary_${profileId}_${period}_${offset}_${activeTab}`;

      if (storage && isStorageReady) {
        const storedPressureSummary = await getIonicStorage(storage, cacheKey);

        // Check if request was aborted before setting state
        if (
          storedPressureSummary &&
          period === selectedTimePeriod &&
          offset === periodOffset &&
          !signal?.aborted
        ) {
          setPressureSummary(storedPressureSummary);
        }
      }

      const response = await getFetchPressureSummary(
        profileId,
        dateRange.startDate,
        dateRange.endDate
      );

      // Check if request was aborted
      if (signal?.aborted) {
        console.log("Pressure summary fetch aborted for period:", period);
        return;
      }

      if (
        response &&
        period === selectedTimePeriod &&
        offset === periodOffset
      ) {
        setPressureSummary(response);
        await ionicStorage(storage, cacheKey, response);
      } else if (response) {
        console.log(
          "Ignoring stale pressure summary for period:",
          period,
          "offset:",
          offset,
          "Current period:",
          selectedTimePeriod,
          "Current offset:",
          periodOffset
        );
      }
    } catch (error: any) {
      if (error.name === "AbortError") {
        console.log("Pressure summary request cancelled");
        return;
      }
      console.error("Error fetching pressure summary:", error);
    }
  }

  // Filter therapies for daily view
  const filteredTherapies = useMemo(() => {
    if (!dailyTherapies?.therapies?.length) return [];

    const therapyType = activeTab === "cough" ? "cough" : "clear";
    return dailyTherapies.therapies.filter(
      (therapy: any) =>
        (therapy.hasPairedDevice || therapy.hadConnectedDevice) &&
        (therapy.type === therapyType ||
          therapy.name?.toLowerCase().includes(therapyType))
    );
  }, [dailyTherapies, activeTab, user?.profile_id, profileId]);

  // Filter data based on active tab
  const filteredData = {
    therapyData: activityAdherence.therapyData.filter((therapy: any) => {
      const therapyType = activeTab === "cough" ? "cough" : "clear";
      return (
        therapy.type === therapyType ||
        therapy.name?.toLowerCase().includes(therapyType)
      );
    }),
  };

  // Determine available tabs based on devices
  const availableTabs = useMemo(() => {
    const tabs: { id: "cough" | "clear"; label: string }[] = [];

    activityAdherence.therapyData.forEach((therapyRecord: any) => {
      const deviceName = therapyRecord.name.toLowerCase();
      if (
        deviceName.includes("cough") &&
        !tabs.some((tab) => tab.id === "cough") &&
        therapyRecord.hadConnectedDevice
      ) {
        tabs.push({ id: "cough", label: "BiWaze® Cough" });
      } else if (
        deviceName.includes("clear") &&
        !tabs.some((tab) => tab.id === "clear") &&
        therapyRecord.hadConnectedDevice
      ) {
        tabs.push({ id: "clear", label: "BiWaze® Clear" });
      }
    });

    return tabs;
  }, [activityAdherence.therapyData]);

  // Update active tab when available tabs change
  useEffect(() => {
    if (
      availableTabs.length > 0 &&
      !availableTabs.some((tab) => tab.id === activeTab)
    ) {
      setActiveTab(availableTabs[0].id);
    }
  }, [availableTabs, activeTab]);
  const noDevicesPaired =
    filteredTherapies?.filter((t: any) => t?.hadConnectedDevice).length === 0;

  // Get therapy type for insights graph
  const therapyTypeForGraph = useMemo(() => {
    if (activeTab.includes("cough")) return "biwaze_cough";
    if (activeTab.includes("clear")) return "biwaze_clear";
    return "biwaze_cough";
  }, [activeTab]);

  // Handle date click from Month/Week/Year view to navigate to appropriate view
  const handleDateClick = useCallback((clickedDate: string) => {
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    // Determine the format of the clicked date to decide navigation
    if (clickedDate.includes('-')) {
      const parts = clickedDate.split('-');

      if (parts.length === 2) {
        // Format: "YYYY-MM" - Year view month click, navigate to Month view
        const [year, month] = parts.map(Number);
        const targetDate = new Date(year, month - 1, 1);
        const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

        // Calculate month offset
        const monthDiff = (targetDate.getFullYear() - currentMonth.getFullYear()) * 12 +
          (targetDate.getMonth() - currentMonth.getMonth());

        setPeriodOffset(monthDiff);
        setSelectedTimePeriod("Month");
      } else if (parts.length === 3) {
        // Format: "YYYY-MM-DD" - Month/Week view day click, navigate to Day view
        const [year, month, day] = parts.map(Number);
        const clickedDateOnly = new Date(year, month - 1, day);

        // Calculate the difference in days from today
        const diffDays = Math.round((clickedDateOnly.getTime() - todayOnly.getTime()) / 86400000);

        // Set the period offset and switch to Day view
        setPeriodOffset(diffDays);
        setSelectedTimePeriod("Day");
      }
    }
  }, []);

  // Handle month picker selection
  const handleMonthPickerSelect = useCallback((monthISO: string) => {
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), 1);
    const [year, month] = monthISO.split("-").map(Number);
    const selectedMonth = new Date(year, month - 1, 1);

    // Calculate month offset
    const monthDiff = (selectedMonth.getFullYear() - todayOnly.getFullYear()) * 12 +
      (selectedMonth.getMonth() - todayOnly.getMonth());

    setPeriodOffset(monthDiff);
    setSelectedTimePeriod("Month");
    setShowMonthPicker(false);
  }, []);

  // Handle year picker selection
  const handleYearPickerSelect = useCallback((year: number) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const selectedYear = new Date(year, 0, 1);

    // Calculate year offset
    const yearDiff = year - currentYear;

    setPeriodOffset(yearDiff);
    setSelectedTimePeriod("Year");
    setShowYearPicker(false);
  }, []);

  // Handle week range picker selection
  const handleWeekRangeSelect = useCallback((startDate: string, endDate: string) => {
    const today = new Date();
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const [year, month, day] = startDate.split("-").map(Number);
    const selectedStartDate = new Date(year, month - 1, day);

    // Find the Monday of the selected week
    const dayOfWeek = selectedStartDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const mondayOfWeek = new Date(selectedStartDate);
    mondayOfWeek.setDate(selectedStartDate.getDate() - mondayOffset);

    // Calculate the difference in days from today's Monday
    const todayDayOfWeek = today.getDay();
    const todayMondayOffset = todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1;
    const todayMonday = new Date(today);
    todayMonday.setDate(today.getDate() - todayMondayOffset);

    // Calculate week offset (difference in weeks)
    const diffDays = Math.round((mondayOfWeek.getTime() - todayMonday.getTime()) / 86400000);
    const weekOffset = Math.round(diffDays / 7);

    // Set the period offset and switch to Week view
    setPeriodOffset(weekOffset);
    setSelectedTimePeriod("Week");
    setShowWeekRangePicker(false);
  }, []);

  // Toggle expanded session accordion
  const toggleSession = (refId: string) => {
    setExpandedSessionId((prev) => (prev === refId ? null : refId));
  };

  // Check if a therapy is a clear type (expandable)
  const isClearTherapy = (therapy: any) => {
    return (
      therapy.type === "clear" ||
      therapy.name?.toLowerCase().includes("clear")
    );
  };

  // Converts a ClearTherapySessionDetail (from API) into ClearTherapyDataPoint for the chart
  const sessionDetailToDataPoint = (session: ClearTherapySessionDetail): ClearTherapyDataPoint => ({
    date: session.date,
    avgNebDuration: session.nebDuration,
    avgPepDuration: session.pepDuration,
    avgOscDuration: session.oscDuration,
    pepPressure: session.pepPressure,
    oscPressure: session.oscPressure,
    summary: {
      sessionCount: 1,
      totalDuration: session.totalDuration,
      totalPepTime: session.pepDuration,
      totalOscTime: session.oscDuration,
      totalNebTime: session.nebDuration,
      totalPepWithNebTime: session.pepWithNebTime,
      totalOscWithNebTime: session.oscWithNebTime,
      totalPhases: session.totalPhases,
      modes: session.mode ? [session.mode] : [],
      nebConnected: session.nebConnected,
    },
  });

  return (
    <IonPage className="!bg-white">
      <IonContent className="min-h-full">
        <Header
          showGreeting={false}
          showUserName={false}
          showAvatar={false}
          showBackButton={true}
          subTitle={subTitle}
          pageTitle="Insights"
          showUserIcon={false}
          showDropDown={false}
          onBackButtonClick={() => history.push("/home")}
        />

        <IonCardContent className="ion-padding min-h-[100dvh] !px-0 gap-4 br-25 ion-content-scroll-host bg-[#F7F5FA] overflow-y-auto">
          {availableTabs.length > 1 && (
            <TabNavigation
              tabs={availableTabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="font-bold"
            />
          )}
          <div className="lg:mx-8 lg:px-8 md:px-2 md:mx-2">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 md:gap-4 px-2">
              <div className="col-span-1">
                <SegmentedPicker
                  options={[
                    { label: "Day", value: "Day" },
                    { label: "Week", value: "Week" },
                    { label: "Month", value: "Month" },
                    { label: "Year", value: "Year" },
                  ]}
                  initialSelected={selectedTimePeriod}
                  selected={selectedTimePeriod}
                  onChange={(value) =>
                    handleTimePeriodChange(value as TimePeriod)
                  }
                // className="mb-2"
                />
                {/* Period Navigation with label */}
                <div className="flex items-center justify-center gap-2 py-3">
                  <button
                    onClick={goToPreviousPeriod}
                    disabled={!canGoBack}
                    className={`p-2 rounded-full transition-colors ${!canGoBack
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:bg-gray-100"
                      }`}
                    aria-label="Previous period"
                  >
                    <IonIcon
                      icon={chevronBack}
                      className="text-xl text-[#00A3E0]"
                    />
                  </button>
                  <span
                    className={`text-base font-semibold text-gray-700 min-w-[180px] text-center ${selectedTimePeriod === "Day" ? "cursor-pointer underline decoration-[#00A3E0] underline-offset-4" :
                      selectedTimePeriod === "Month" ? "cursor-pointer underline decoration-[#00A3E0] underline-offset-4" :
                        selectedTimePeriod === "Year" ? "cursor-pointer underline decoration-[#00A3E0] underline-offset-4" :
                          selectedTimePeriod === "Week" ? "cursor-pointer underline decoration-[#00A3E0] underline-offset-4" : ""
                      }`}
                    onClick={() => {
                      if (selectedTimePeriod === "Day") {
                        setShowDatePicker(true);
                      } else if (selectedTimePeriod === "Month") {
                        setShowMonthPicker(true);
                      } else if (selectedTimePeriod === "Year") {
                        setShowYearPicker(true);
                      } else if (selectedTimePeriod === "Week") {
                        setShowWeekRangePicker(true);
                      }
                    }}
                  >
                    {currentPeriodLabel}
                  </span>
                  <button
                    onClick={goToNextPeriod}
                    disabled={periodOffset >= 0}
                    className={`p-2 rounded-full transition-colors ${periodOffset >= 0
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:bg-gray-100"
                      }`}
                    aria-label="Next period"
                  >
                    <IonIcon
                      icon={chevronForward}
                      className="text-xl text-[#00A3E0]"
                    />
                  </button>
                </div>
                {selectedTimePeriod === "Day" && (
                  <DayDatePicker
                    isOpen={showDatePicker}
                    onClose={() => setShowDatePicker(false)}
                    maxDate={todayISO}
                    minDate={oldestTherapyDate?.[activeTab] || undefined}
                    initialDate={generateDateRange("Day", periodOffset).startDate}
                    onSelect={(dateISO) => {
                      const today = new Date();
                      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const [y, m, d] = dateISO.split("-").map(Number);
                      const sel = new Date(y, m - 1, d);
                      const diffDays = Math.round((sel.getTime() - todayOnly.getTime()) / 86400000);
                      setPeriodOffset(diffDays);
                      setShowDatePicker(false);
                    }}
                  />
                )}
                {selectedTimePeriod === "Month" && (
                  <MonthPicker
                    isOpen={showMonthPicker}
                    onClose={() => setShowMonthPicker(false)}
                    maxMonth={todayISO.slice(0, 7)}
                    minMonth={oldestTherapyDate?.[activeTab]?.slice(0, 7) || undefined}
                    initialMonth={generateDateRange("Month", periodOffset).startDate.slice(0, 7)}
                    onSelect={handleMonthPickerSelect}
                  />
                )}
                {selectedTimePeriod === "Year" && (
                  <YearPicker
                    isOpen={showYearPicker}
                    onClose={() => setShowYearPicker(false)}
                    maxYear={new Date().getFullYear()}
                    minYear={oldestTherapyDate?.[activeTab] ? parseInt(oldestTherapyDate[activeTab].split("-")[0]) : undefined}
                    initialYear={parseInt(generateDateRange("Year", periodOffset).startDate.slice(0, 4))}
                    onSelect={handleYearPickerSelect}
                  />
                )}
                {selectedTimePeriod === "Week" && (
                  <WeekRangePicker
                    isOpen={showWeekRangePicker}
                    onClose={() => setShowWeekRangePicker(false)}
                    maxDate={todayISO}
                    minDate={oldestTherapyDate?.[activeTab] || undefined}
                    initialDate={generateDateRange("Week", periodOffset).startDate}
                    onSelect={handleWeekRangeSelect}
                  />
                )}
              </div>
              <div className="col-span-1 flex justify-center">
                <IonButton
                  expand="block"
                  onClick={() =>
                    history.push(
                      `/activity/send-report?id=${profileId}&name=${subTitle}&startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&period=${currentPeriodLabel}&tab=${activeTab}&oldestCough=${oldestTherapyDate?.cough || ""}&oldestClear=${oldestTherapyDate?.clear || ""}`
                    )
                  }
                  className="primary-button mt-2 px-4"
                  style={{
                    "--border-radius": "30px",
                    "--background": "#00A3E0",
                    height: "46px",
                    margin: "15px",
                    width: "95%",
                  }}
                >
                  Send Report
                </IonButton>
              </div>
            </div>

            <motion.div
              key={`${selectedTimePeriod}-${periodOffset}`}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2 }}
              className="touch-pan-y"
            >
              {selectedTimePeriod === "Day" && !loading && (
                <>
                  <div className="text-center p-4 text-base font-semibold text-[#00A3E0] mb-2">
                    {/* {`Today's Summary - ${new Date().toLocaleString("default", {
                  month: "long",
                })} ${new Date().getDate()}, ${new Date().getFullYear()}`} */}
                  </div>
                  <div className="col-span-1 lg:col-span-2">
                    <div className="mt-2 col-span-1">
                      <TherapyActivitiesSection
                        title={
                          periodOffset === 0
                            ? "Today's Therapies"
                            : `${currentPeriodLabel}'s Therapies`
                        }
                        filteredTherapies={filteredTherapies}
                        activityAdherence={dailyTherapies}
                        formattedDate={dateRange.startDate}
                        noDevicesPaired={noDevicesPaired}
                      />
                    </div>
                    {/* {!filteredTherapies.some(
                (therapy: any) => therapy.completed?.length > 0
              ) && (
                <div className="flex m-2 p-4 md:mt-4 justify-center">
                  <IonButton
                    expand="block"
                    onClick={() => setShowDaySummary((prev) => !prev)}
                    className="primary-button mt-4 px-4 py-4 font-semibold text-base"
                    style={{
                      "--border-radius": "30px",
                      "--background": "#00A3E0",
                      height: "46px",
                      margin: "15px",
                      width: "95%",
                    }}
                  >
                    {showDaySummary ? "Hide Summary" : "View Summary"}
                  </IonButton>
                </div>
              )} */}
                    {
                      <>
                        <div className="text-center p-4 mt-2 text-base font-semibold text-[#00A3E0] mb-2">
                          {/* {`Therapy Completion Summary - ${new Date().toLocaleString(
                      "default",
                      { month: "long" }
                    )} ${new Date().getDate()}, ${new Date().getFullYear()}`} */}
                        </div>
                        <div className="px-2 md:px-8">
                          <div className="space-y-4">
                            {/* Clear therapy: stacked overview chart showing all sessions */}
                            {showDaySummary && clearSessionsMap.size > 0 && (
                              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-4 pt-3 pb-1 flex items-center justify-between">
                                  <span className="text-sm font-semibold text-gray-700">
                                    {periodOffset === 0 ? "Today's" : currentPeriodLabel + "'s"} Clear Sessions
                                  </span>
                                  <span className="text-xs text-gray-400">{clearSessionsMap.size} session{clearSessionsMap.size !== 1 ? "s" : ""}</span>
                                </div>
                                <div className="px-2 pb-3">
                                  <ClearDaySessionsChart
                                    sessions={Array.from(clearSessionsMap.values())}
                                    activeRefId={expandedSessionId}
                                    onSessionClick={(refId) =>
                                      setExpandedSessionId((prev) => (prev === refId ? null : refId))
                                    }
                                  />
                                </div>
                              </div>
                            )}

                            {(() => {
                              const noTherapies = filteredTherapies.length === 0;
                              const noCoughSessions =
                                activeTab === "cough" &&
                                filteredTherapies.length > 0 &&
                                !filteredTherapies.some(
                                  (t: any) => t.completed?.length > 0
                                );
                              const noClearSessions =
                                activeTab === "clear" &&
                                !clearSessionsLoading &&
                                clearSessionsMap.size === 0;
                              if (noTherapies || noCoughSessions || noClearSessions) {
                                return (
                                  <div className="bg-white p-6 rounded-2xl shadow-sm text-center text-gray-400 text-sm md:text-lg border border-gray-100">
                                    No completed therapies for{" "}
                                    {periodOffset === 0 ? "today" : currentPeriodLabel}.
                                  </div>
                                );
                              }
                              return null;
                            })()}

                            {filteredTherapies.map(
                              (therapy: any, index: number) => {
                                // ── Clear therapy: render directly from clearSessionsMap
                                //    so each card shows actual per-session values (not day averages)
                                if (isClearTherapy(therapy)) {
                                  if (clearSessionsLoading) {
                                    return <CardSkeleton key={`clear-load-${index}`} count={1} />;
                                  }
                                  const clearSessions = Array.from(clearSessionsMap.values());
                                  if (clearSessions.length === 0) return null;
                                  return clearSessions.map((clearSession, idx) => {
                                    const sessionKey = clearSession.refId;
                                    const isExpanded = expandedSessionId === sessionKey;
                                    const sessionDataPoint = sessionDetailToDataPoint(clearSession);
                                    return (
                                      <div key={`clear-${index}-${idx}`} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                        {/* ── Collapsed header (matches Cough style) ── */}
                                        <div
                                          className="p-4 cursor-pointer hover:bg-gray-50 transition-all duration-200"
                                          onClick={() => toggleSession(sessionKey)}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm md:text-base text-gray-700 font-medium">
                                                      {getSessionLabel(clearSession.startTime)}
                                                    </p>
                                                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                                                      {clearSession.refId}
                                                    </span>
                                                    {clearSession.mode && (
                                                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
                                                        {clearSession.mode}
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                                <div>
                                                  {clearSession.totalDuration > 0 && (
                                                    <p className="text-sm md:text-base text-gray-700 font-medium">
                                                      {formatDuration(clearSession.totalDuration)} total
                                                    </p>
                                                  )}
                                                  {clearSession.totalPhases > 0 && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                      {clearSession.totalPhases} phases
                                                    </p>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="ml-4 flex-shrink-0">
                                              <IonIcon
                                                icon={isExpanded ? chevronUp : chevronDown}
                                                className="text-xl text-[#00A3E0]"
                                              />
                                            </div>
                                          </div>
                                        </div>

                                        {/* ── Expanded section ── */}
                                        {isExpanded && (
                                          <div className="border-t border-gray-100 bg-gray-50 p-4">
                                            <ClearSessionPressureChart
                                              profileId={profileId}
                                              date={dateRange.startDate}
                                              sessionData={sessionDataPoint}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    );
                                  });
                                }

                                // ── Cough therapy: existing behaviour
                                return therapy.completed?.length > 0 &&
                                  therapy.completed.map(
                                    (session: any, idx: number) => {
                                      const sessionKey = session.ts_ref_id
                                        ? String(session.ts_ref_id)
                                        : `session-${index}-${idx}`;
                                      const isExpanded = expandedSessionId === sessionKey;
                                      const cyclesData = pressureFlowData.get(String(session.ts_ref_id)) || [];
                                      const numberOfCycles = cyclesData.length;
                                      return (
                                        <div key={`${index}-${idx}`} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                          <div
                                            className="p-4 cursor-pointer hover:bg-gray-50 transition-all duration-200"
                                            onClick={() => toggleSession(sessionKey)}
                                          >
                                            <div className="flex items-center justify-between">
                                              <div className="flex-1">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                  <div>
                                                    <div className="mt-3 flex items-center gap-2">
                                                      <p className="text-sm md:text-base text-gray-700 font-medium">
                                                        {formatSessionTime(session.date, (user as any)?.time_zone)}
                                                      </p>
                                                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                                                        {session.ts_ref_id || "N/A"}
                                                      </span>
                                                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                                                        {session.device_id}
                                                      </span>
                                                    </div>
                                                  </div>
                                                  <div>
                                                    <p className="text-sm md:text-base text-gray-700 font-medium">
                                                      {cyclesData[0]?.profileName || ""}
                                                    </p>
                                                    <p className="text-xs text-gray-500 mt-1">
                                                      {`${numberOfCycles} cycles`}
                                                    </p>
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="ml-4 flex-shrink-0">
                                                <IonIcon
                                                  icon={isExpanded ? chevronUp : chevronDown}
                                                  className="text-xl text-[#00A3E0]"
                                                />
                                              </div>
                                            </div>
                                          </div>
                                          {isExpanded && (
                                            <div className="border-t border-gray-100 bg-gray-50 p-4">
                                              {pressureFlowLoading ? (
                                                <div className="flex items-center justify-center h-[250px]">
                                                  <IonSpinner name="crescent" className="text-[#00A3E0]" />
                                                </div>
                                              ) : (
                                                <SessionPressureChart
                                                  cycles={cyclesData}
                                                  selectedMetric={sessionMetric}
                                                  onMetricChange={setSessionMetric}
                                                />
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    }
                                  );
                              }
                            )}
                          </div>
                        </div>
                      </>
                    }
                  </div>
                </>
              )}
              {loading && <CardSkeleton count={4} />}
              {!loading && selectedTimePeriod !== "Day" && (
                <>
                  <div className="inset-x-0 bottom-0 bg-white rounded-t-[25px] -z-10"></div>
                  <div className="relative z-0 grid grid-cols-1 px-2 overflow-visible transition-all duration-500 ease-in-out">
                    <div className="col-span-1 md:col-span-1 lg:col-span-1">
                      {filteredData.therapyData.map(
                        (therapy: any, index: number) => (
                          <BiWazeTherapyCard
                            key={`therapy-${index}`}
                            title=""
                            currentMonth={selectedPeriodMonth}
                            therapies={[therapy]}
                            adherenceHeader={
                              selectedTimePeriod === "Week"
                                ? "Weekly Adherence"
                                : selectedTimePeriod === "Month"
                                  ? "Monthly Adherence"
                                  : "Yearly Adherence"
                            }
                            showHeader={false}
                            hideFooter={true}
                            timePeriod={selectedTimePeriod as any}
                            oldestKnownTherapyDate={
                              activityAdherence.oldestKnownTherapyDate
                            }
                            onDateClick={handleDateClick}
                          />
                        )
                      )}
                    </div>

                    {activityAdherence?.insights && (
                      <div className="margin-auto max-w-[100%] mb-4">
                        <InsightsGraphVisx
                          insightsData={activityAdherence.insights}
                          className="m-0"
                          showGraphs={[therapyTypeForGraph]}
                          title="Therapy Overview"
                        />
                      </div>
                    )}
                  </div>

                  {activeTab !== "clear" && (
                    <div>
                      <div onPointerDownCapture={(e) => e.stopPropagation()}>
                        {" "}
                        <PressureFlowGraph
                          profileId={profileId}
                          dateRange={dateRange}
                        />
                      </div>
                    </div>
                  )}
                  {activeTab === "clear" && (
                    <div onPointerDownCapture={(e) => e.stopPropagation()}>
                      <ClearPressureFlowGraph
                        profileId={profileId}
                        dateRange={dateRange}
                      />
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </div>
        </IonCardContent>
      </IonContent>
    </IonPage>
  );
};

export default ActivityDetails;
